import { animated, useSpring } from 'react-spring';

import Card from 'components/_shared/Card';

import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme: { colors: { DarkPurple02: string } }) => ({
    bonusCardContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '20px',
        width: '100%',
        marginTop: '2px'
    },
    bonusCardTracker: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: '20px',
        boxShadow: '0px 5px 40px 0px rgba(0, 0, 0, 0.10)',
        marginTop: '0 !important',
        overflow: 'hidden',
        padding: '20px',
        width: '90%'
    },
    fundingBanner: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        backgroundColor: '#E7EFFE',
        borderRadius: '16px 16px 0 0',
        color: '#062D7A',
        fontSize: '13px',
        fontWeight: 700,
        lineHeight: '18px',
        margin: '-20px -20px 0 -20px',
        padding: '10px 16px',
        textAlign: 'center',
        width: 'calc(100% + 40px)',
        boxSizing: 'border-box'
    },
    trackerBody: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: '20px',
        width: '100%'
    },
    bonusCardTrackerTitle: {
        color: theme.colors.DarkPurple02,
        fontSize: '20px',
        fontWeight: '800',
        alignSelf: 'flex-start'
    },
    progressBarContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        margin: '16px 0 4px 0',
        width: '100%'
    },
    progressBar: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '10px',
        height: '18px',
        width: '100%'
    },
    dot: {
        position: 'absolute',
        borderRadius: '50%',
        height: '12px',
        width: '12px'
    },
    progressBarOverlay: {
        maxWidth: '98%',
        marginLeft: '1%',
        height: '12px',
        borderRadius: '10px',
        position: 'absolute'
    },
    progressBarBackground: {
        backgroundColor: '#F3F3F3',
        borderRadius: '10px',
        height: '18px',
        width: '100%'
    },
    maxAmount: {
        whiteSpace: 'nowrap'
    },
    assigned: {
        display: 'flex',
        fontSize: '16px',
        whiteSpace: 'nowrap',
        alignSelf: 'flex-start'
    },
    // Mirror shared Card sizing (margin/padding/width) without Card's white !important bg
    lockedContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '30px',
        backgroundColor: '#F6F7FB',
        borderRadius: '16px',
        boxShadow: '0px 5px 40px 0px rgba(0, 0, 0, 0.10)',
        color: '#1F1F51',
        margin: '0 20px 20px 20px',
        padding: '20px',
        width: '90%',
        textAlign: 'left'
    },
    lockedIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: 'center',
        backgroundColor: '#E8EAF4',
        borderRadius: '50%',
        color: '#A6A6BF',
        minWidth: '40px',
        height: '40px',
        flexShrink: 0,
        '& i': {
            fontSize: '16px',
            lineHeight: 1
        }
    },
    lockedContent: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: '6px'
    },
    lockedTitleRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '8px'
    },
    lockedTitle: {
        fontSize: '20px',
        fontWeight: 700,
        lineHeight: '27px',
        color: '#A6A6BF'
    },
    lockedPill: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#E8EAF4',
        borderRadius: '999px',
        color: '#A6A6BF',
        fontSize: '12px',
        fontWeight: 800,
        padding: '4px 8px',
        width: 'fit-content'
    },
    lockedDescription: {
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '22px',
        color: '#A6A6BF',
        textAlign: 'left'
    }
}));

type ExtraBonusTrackerCardProps = {
    maxBonusAmount: number;
    remainingBonusAmount: number;
    baseRemainingAmount: number;
    baseLimit: number;
};

const ExtraBonusTrackerCard: React.FC<ExtraBonusTrackerCardProps> = ({
    maxBonusAmount,
    remainingBonusAmount,
    baseRemainingAmount,
    baseLimit
}) => {
    const classes = useStyles();

    const isLocked = baseRemainingAmount > 0;
    const assigned = maxBonusAmount - remainingBonusAmount;
    const progressPercentage =
        maxBonusAmount > 0 ? (assigned * 98) / maxBonusAmount : 0;
    const progressBarOverlayWidth = `${progressPercentage}%`;
    const month = new Date().toLocaleString('default', { month: 'long' });
    const title = `Extra ${month} Bonuses`;

    const progressBarAnimation = useSpring({
        width: `${progressPercentage}%`,
        config: { tension: 120, friction: 30 }
    });

    if (maxBonusAmount <= 0) {
        return null;
    }

    if (isLocked) {
        return (
            <div className={classes.bonusCardContainer}>
                <div className={classes.lockedContainer}>
                    <div className={classes.lockedIcon}>
                        <i className="fas fa-lock" />
                    </div>
                    <div className={classes.lockedContent}>
                        <div className={classes.lockedTitleRow}>
                            <span className={classes.lockedTitle}>{title}</span>
                            <span className={classes.lockedPill}>
                                + ${maxBonusAmount}
                            </span>
                        </div>
                        <span className={classes.lockedDescription}>
                            Assign all ${baseLimit} in the base pool to unlock
                            the extra ${maxBonusAmount} bonus pool
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    const getAssignedAmount = () => {
        if (remainingBonusAmount === 0) {
            return `🎊 All bonuses assigned`;
        }

        return `$${assigned} assigned`;
    };

    return (
        <div className={classes.bonusCardContainer}>
            <Card className={classes.bonusCardTracker}>
                <span className={classes.fundingBanner}>
                    <span>✨</span>
                    <span>
                        Funded by Multiply Capital Foundation for limited time
                        only
                    </span>
                    <span>✨</span>
                </span>
                <div className={classes.trackerBody}>
                    <span className={classes.bonusCardTrackerTitle}>
                        {title}
                    </span>
                    <div className={classes.progressBarContainer}>
                        <div className={classes.progressBar}>
                            <animated.span
                                className={classes.progressBarOverlay}
                                style={{
                                    ...progressBarAnimation,
                                    backgroundColor:
                                        remainingBonusAmount <= 0
                                            ? '#E7FEEB'
                                            : 'rgb(153,157,255, 0.7)'
                                }}
                            >
                                <span
                                    className={classes.dot}
                                    style={{
                                        backgroundColor:
                                            remainingBonusAmount <= 0
                                                ? '#0AA823'
                                                : '#999DFF',
                                        right: assigned <= 0 ? '-12px' : '0px'
                                    }}
                                />
                            </animated.span>
                            <span className={classes.progressBarBackground} />
                        </div>
                        <span className={classes.maxAmount}>
                            ${maxBonusAmount} max
                        </span>
                    </div>
                    <span
                        className={classes.assigned}
                        style={{
                            justifyContent:
                                assigned <= 0 ? 'flex-start' : 'flex-end',
                            paddingLeft:
                                progressBarOverlayWidth === '98%'
                                    ? '16px'
                                    : '0',
                            width:
                                assigned <= 0
                                    ? '20%'
                                    : progressBarOverlayWidth
                        }}
                    >
                        {getAssignedAmount()}
                    </span>
                </div>
            </Card>
        </div>
    );
};

export default ExtraBonusTrackerCard;
